import { NavLink, useLocation } from 'react-router-dom';
import { ENGINES, MARKETPLACE, isLive, type Engine } from '@/engines/registry';
import { engineStatusLabel, engineConnectionState } from '@/data/module-connection-status';
import { useAuth } from '@/auth/AuthContext';
import { InstrumentBezelIcon } from '@/components/identity';
import { cn } from '@/lib/utils';

/* ----------------------------------------------------------------------------
 * Left engine rail — the OS spine (v1.1 §4). On desktop (>= lg) it is the
 * always-visible icon rail (unchanged). Below lg it is hidden and its content
 * is rendered via `variant="drawer"` inside the MobileDrawer as full-width,
 * labelled rows. Executive OS sits ABOVE the engines; reserved engines +
 * Marketplace sit below a divider, demoted. Rail order is fixed.
 * -------------------------------------------------------------------------- */

function RailIcon({ engine, active, entitled }: { engine: Engine; active: boolean; entitled: boolean }) {
  const live = isLive(engine.status);
  const locked = !live || !entitled;
  return (
    <NavLink
      to={engine.base}
      data-engine={engine.key}
      title={`${engine.name}${locked ? ` · ${engineStatusLabel(engine)}` : ''}`}
      aria-label={`${engine.name}${locked ? ` (${engineStatusLabel(engine)})` : ''}`}
      className={cn(
        'group relative flex h-11 w-11 items-center justify-center rounded-md transition-colors duration-fast ease-calibration',
        active ? 'bg-engine-wash' : 'hover:bg-hover',
        locked && !active && 'opacity-45',
      )}
    >
      {/* active identity edge — the thin engine keyline */}
      <span
        className={cn(
          'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-full bg-engine transition-opacity duration-fast',
          active ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden
      />
      <InstrumentBezelIcon glyph={engine.icon} active={active} size={24} />
      {locked && (
        <span className="absolute bottom-0.5 right-0.5 text-[8px] text-text-tertiary" aria-hidden>
          {engineConnectionState(engine) === 'planned-not-built' ? '◦' : '•'}
        </span>
      )}
    </NavLink>
  );
}

/* Full-width labelled row — mobile drawer only. >= 44px tap target. */
function DrawerRow({
  engine,
  active,
  entitled,
  onNavigate,
}: {
  engine: Engine;
  active: boolean;
  entitled: boolean;
  onNavigate?: () => void;
}) {
  const live = isLive(engine.status);
  const locked = !live || !entitled;
  return (
    <NavLink
      to={engine.base}
      onClick={onNavigate}
      data-engine={engine.key}
      aria-label={`${engine.name}${locked ? ` (${engineStatusLabel(engine)})` : ''}`}
      className={cn(
        'flex min-h-[44px] items-center gap-3 rounded-md px-2 py-1.5 transition-colors duration-fast',
        active ? 'bg-engine-wash text-engine' : 'text-text-secondary hover:bg-hover hover:text-text-primary',
        locked && !active && 'opacity-60',
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center">
        <InstrumentBezelIcon glyph={engine.icon} active={active} size={22} />
      </span>
      <span className="flex-1 truncate text-sm font-medium">{engine.name}</span>
      {locked && (
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-text-tertiary">
          {engineStatusLabel(engine)}
        </span>
      )}
    </NavLink>
  );
}

export function EngineRail({
  variant = 'rail',
  onNavigate,
}: {
  variant?: 'rail' | 'drawer';
  onNavigate?: () => void;
} = {}) {
  const location = useLocation();
  const { activeWorkspace } = useAuth();
  const entitlements = activeWorkspace?.entitlements ?? [];

  const vertical = ENGINES.filter((e) => e.group === 'vertical');
  const crossEngine = ENGINES.filter((e) => e.group === 'cross-engine');
  const reserved = ENGINES.filter((e) => e.group === 'reserved');

  const isActive = (base: string) =>
    location.pathname === base || location.pathname.startsWith(base + '/');

  if (variant === 'drawer') {
    const rows = (engines: Engine[]) =>
      engines.map((e) => (
        <DrawerRow
          key={e.key}
          engine={e}
          active={isActive(e.base)}
          entitled={entitlements.includes(e.key)}
          onNavigate={onNavigate}
        />
      ));
    return (
      <nav aria-label="Engines" className="flex flex-col gap-0.5 p-2">
        <NavLink
          to="/"
          end
          onClick={onNavigate}
          aria-label="Executive OS — Home"
          className={({ isActive: a }) =>
            cn(
              'flex min-h-[44px] items-center gap-3 rounded-md px-2 py-1.5 text-sm font-medium transition-colors duration-fast',
              a ? 'bg-inset text-text-display' : 'text-text-secondary hover:bg-hover hover:text-text-primary',
            )
          }
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center text-lg" aria-hidden>◎</span>
          <span className="flex-1 truncate">Executive OS</span>
        </NavLink>
        <div className="my-1 h-px bg-border-subtle" />
        {rows(vertical)}
        <div className="my-1 h-px bg-border-subtle" />
        {rows(crossEngine)}
        <div className="my-1 h-px bg-border-default" />
        {rows(reserved)}
        <NavLink
          to={MARKETPLACE.base}
          onClick={onNavigate}
          aria-label={`${MARKETPLACE.name} (reserved)`}
          className={({ isActive: a }) =>
            cn(
              'flex min-h-[44px] items-center gap-3 rounded-md px-2 py-1.5 text-sm transition-colors duration-fast',
              a ? 'bg-inset text-text-primary' : 'text-text-tertiary opacity-70 hover:bg-hover',
            )
          }
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center text-lg" aria-hidden>
            {MARKETPLACE.icon}
          </span>
          <span className="flex-1 truncate">{MARKETPLACE.name}</span>
          <span className="shrink-0 text-[10px] uppercase tracking-wide text-text-tertiary">reserved</span>
        </NavLink>
      </nav>
    );
  }

  const renderGroup = (engines: Engine[]) =>
    engines.map((e) => (
      <RailIcon
        key={e.key}
        engine={e}
        active={isActive(e.base)}
        entitled={entitlements.includes(e.key)}
      />
    ));

  return (
    <nav
      aria-label="Engines"
      className="hidden w-14 shrink-0 flex-col items-center gap-1 border-r border-border-subtle bg-surface py-3 lg:flex"
    >
      {/* Executive OS — home, above the engines. Distinct compass glyph + wash,
          deliberately NOT an engine-style icon (v1.1 §4). */}
      <NavLink
        to="/"
        end
        title="Executive OS — Home"
        aria-label="Executive OS — Home"
        className={({ isActive: a }) =>
          cn(
            'flex h-11 w-11 items-center justify-center rounded-md text-lg transition-colors duration-fast ease-calibration',
            a
              ? 'bg-inset text-text-display ring-1 ring-border-default'
              : 'bg-inset/60 text-text-secondary hover:bg-inset hover:text-text-primary',
          )
        }
      >
        <span aria-hidden>◎</span>
      </NavLink>

      <div className="my-1 h-px w-6 bg-border-subtle" />
      {renderGroup(vertical)}
      <div className="my-1 h-px w-6 bg-border-subtle" />
      {renderGroup(crossEngine)}
      <div className="mt-auto flex flex-col items-center gap-1">
        {/* reserved divider — engines below are architecture, visually demoted */}
        <div className="my-1 h-px w-6 bg-border-default" />
        {renderGroup(reserved)}
        {/* Marketplace — a distribution layer, not an engine: distinct framed glyph */}
        <NavLink
          to={MARKETPLACE.base}
          title={`${MARKETPLACE.name} · reserved`}
          aria-label={`${MARKETPLACE.name} (reserved)`}
          className={({ isActive: a }) =>
            cn(
              'flex h-11 w-11 items-center justify-center rounded-md border border-dashed border-border-default text-lg opacity-55 transition-colors duration-fast',
              a ? 'bg-inset text-text-primary opacity-100' : 'text-text-tertiary hover:bg-hover',
            )
          }
        >
          <span aria-hidden>{MARKETPLACE.icon}</span>
        </NavLink>
      </div>
    </nav>
  );
}
